import React, { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const { lang, setLang, theme, setTheme } = useI18n();
  const [name, setName] = useState(user?.name || "");
  const [prefs, setPrefs] = useState({ notification_email: true, notification_inapp: true, timezone: "UTC" });
  const [pwd, setPwd] = useState({ current: "", next: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setName(r.data.user.name || "");
      setPrefs((p) => ({ ...p, ...(r.data.preferences || {}) }));
    });
  }, []);

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.put("/settings", { name, ...prefs, language: lang });
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      refreshUser();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const changePassword = async () => {
    if (!pwd.current || !pwd.next || pwd.next.length < 8) { toast.error("Enter current & new (min 8) password"); return; }
    setBusy(true);
    try {
      await api.post("/settings/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success(lang === "ar" ? "تم تحديث كلمة المرور" : "Password updated");
      setPwd({ current: "", next: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="font-display text-3xl">{lang === "ar" ? "الإعدادات" : "Settings"}</h1>
        <p className="text-muted-foreground text-sm mt-1">{lang === "ar" ? "ملفك الشخصي، التفضيلات، والأمان." : "Your profile, preferences, and security."}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="nx-card p-5" data-testid="settings-profile">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{lang === "ar" ? "الملف الشخصي" : "Profile"}</div>
          <label className="block text-xs mb-3">
            <span className="text-muted-foreground">{lang === "ar" ? "الاسم" : "Full name"}</span>
            <input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm" />
          </label>
          <label className="block text-xs mb-3">
            <span className="text-muted-foreground">{lang === "ar" ? "البريد" : "Email"}</span>
            <input value={user?.email || ""} disabled
              className="mt-1 w-full bg-muted border border-border rounded-sm px-3 py-2 text-sm text-muted-foreground" />
          </label>
          <label className="block text-xs mb-3">
            <span className="text-muted-foreground">{lang === "ar" ? "المنطقة الزمنية" : "Timezone"}</span>
            <input data-testid="settings-tz" value={prefs.timezone} onChange={(e) => setPrefs({ ...prefs, timezone: e.target.value })}
              className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm" />
          </label>
          <button data-testid="settings-save-profile-btn" onClick={saveProfile} disabled={busy} className="nx-btn-primary">{lang === "ar" ? "حفظ" : "Save"}</button>
        </div>

        <div className="nx-card p-5" data-testid="settings-appearance">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{lang === "ar" ? "المظهر واللغة" : "Appearance & language"}</div>
          <div className="mb-3">
            <div className="text-xs text-muted-foreground mb-1">{lang === "ar" ? "اللغة" : "Language"}</div>
            <div className="inline-flex bg-background border border-border rounded-sm p-1">
              <button data-testid="settings-lang-en" onClick={() => setLang("en")} className={`px-4 py-1.5 text-xs rounded-sm ${lang === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>English</button>
              <button data-testid="settings-lang-ar" onClick={() => setLang("ar")} className={`px-4 py-1.5 text-xs rounded-sm ${lang === "ar" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>العربية</button>
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">{lang === "ar" ? "المظهر" : "Theme"}</div>
            <div className="inline-flex bg-background border border-border rounded-sm p-1">
              <button data-testid="settings-theme-light" onClick={() => setTheme("light")} className={`px-4 py-1.5 text-xs rounded-sm ${theme === "light" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Light</button>
              <button data-testid="settings-theme-dark" onClick={() => setTheme("dark")} className={`px-4 py-1.5 text-xs rounded-sm ${theme === "dark" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Dark</button>
            </div>
          </div>
        </div>

        <div className="nx-card p-5" data-testid="settings-notifications">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{lang === "ar" ? "الإشعارات" : "Notifications"}</div>
          <label className="flex items-center justify-between py-2">
            <span className="text-sm">{lang === "ar" ? "بريد إلكتروني" : "Email notifications"}</span>
            <input data-testid="settings-notif-email" type="checkbox" checked={!!prefs.notification_email}
              onChange={(e) => setPrefs({ ...prefs, notification_email: e.target.checked })}
              className="w-4 h-4 accent-[#002FA7]" />
          </label>
          <label className="flex items-center justify-between py-2">
            <span className="text-sm">{lang === "ar" ? "داخل التطبيق" : "In-app notifications"}</span>
            <input data-testid="settings-notif-inapp" type="checkbox" checked={!!prefs.notification_inapp}
              onChange={(e) => setPrefs({ ...prefs, notification_inapp: e.target.checked })}
              className="w-4 h-4 accent-[#002FA7]" />
          </label>
        </div>

        <div className="nx-card p-5" data-testid="settings-security">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{lang === "ar" ? "الأمان" : "Security"}</div>
          <label className="block text-xs mb-3">
            <span className="text-muted-foreground">{lang === "ar" ? "كلمة المرور الحالية" : "Current password"}</span>
            <input data-testid="settings-current-pwd" type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
              className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm" />
          </label>
          <label className="block text-xs mb-4">
            <span className="text-muted-foreground">{lang === "ar" ? "كلمة المرور الجديدة" : "New password"}</span>
            <input data-testid="settings-new-pwd" type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
              className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm" />
          </label>
          <button data-testid="settings-change-pwd-btn" onClick={changePassword} disabled={busy} className="nx-btn-primary">{lang === "ar" ? "تحديث" : "Update"}</button>
        </div>
      </div>
    </AppShell>
  );
}
